/* eslint-disable indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {uuid} from '../../../../utils/utils';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import SafetyService from '../../../../services/safety.service';
import {buildGroupPathString} from '../../../../utils/group-utils';
import {sortSafetyModelsByGroupPath} from '../../../../utils/safety-model-utils';
import {
    CreateIntegratedRiskPictureDTO, IntegratedRiskPicture, IRPReportSearchCriteria
} from '../../../../@types/integrated-risk-picture';
import SafetyModel from '../../../../@types/safety-model';
import EventSequenceSummary from '../../../../@types/event-sequence-summary';
import newOccurrenceService from '../../../../services/new-occurrence.service';
import OccurrenceReportDescriptorService from '../../../../services/occurrence-report-descriptor.service';
import IRPService from '../../../../services/irp.service';
import {IntegratedRiskPictureEditorProps} from '../../../../@types/editor-props';
import OccurrenceReportDescriptor, {
    OccurrenceReportDescriptorTerm
} from '../../../../@types/occurrence-report-descriptor';
import {ResourceType} from '../../../../@types/resource-type';
import {toast} from 'react-toastify';


/**
 * Interface for render items in safety model dropdown
 * Used to mix dividers and model items for visual grouping
 */
interface SafetyModelRenderItem {
    isDivider: boolean;
    key: string;
    model?: SafetyModel;
    groupPath?: string;
}

/**
 * Display details for a selected descriptor term chip, derived from the
 * selected term IDs (the single source of truth for the descriptor filter).
 */
interface DescriptorTermChip {
    id: string;
    name: string;
    definition: string;
}

// Throughput observed in production runs: ~50 reports processed in parallel,
// 30-60s for a fully mapped report, ~12s for a non-mapped one. Reports move
// through in waves of up to PARALLEL_REPORT_COUNT, averaging ~25s per wave,
// which converges to roughly 2 reports per second on long runs. Small runs do
// not benefit from parallelism below the wave size, so estimates are floored
// at one full pipeline pass (30-60s).
const PARALLEL_REPORT_COUNT = 50;
const AVERAGE_WAVE_SECONDS = 25;
const MIN_PIPELINE_SECONDS = 30;
const MAX_PIPELINE_SECONDS = 60;

const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (remainder === 0) {
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return `${hours}h ${remainder}m`;
};

const IntegratedRiskPictureEditor: React.FC<IntegratedRiskPictureEditorProps> = (props) => {
    const { integratedRiskPicture, isActive, updateTab  } = props;
    
    // Form state
    const [name, setName] = useState(integratedRiskPicture?.name || '');
    const [description, setDescription] = useState(integratedRiskPicture?.description || '');
    const [selectedSafetyModelId, setSelectedSafetyModelId] = useState<string>('');
    const [selectedEventSequenceId, setSelectedEventSequenceId] = useState<string>('');
    const [selectedDescriptorId, setSelectedDescriptorId] = useState<string>('');
    const [selectedDescriptorTermIds, setSelectedDescriptorTermIds] = useState<string[]>([]);
    const [includeAllReports, setIncludeAllReports] = useState(true);
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [reportLimit, setReportLimit] = useState<number | null>(null);
    const [reportSources, setReportSources] = React.useState<any[]>([]);
    const [sourcesLoading, setSourcesLoading] = React.useState(false);
    // Preview state
    const [reportCount, setReportCount] = useState<number | null>(null);
    const [countLoading, setCountLoading] = useState(false);
    const [estimatedTime, setEstimatedTime] = useState<string | null>(null);
    // Data state
    const [safetyModels, setSafetyModels] = useState<SafetyModel[]>([]);
    const [eventSequences, setEventSequences] = useState<EventSequenceSummary[]>([]);
    const [publishedDescriptors, setPublishedDescriptors] = useState<OccurrenceReportDescriptor[]>([]);
    const [descriptorTerms, setDescriptorTerms] = useState<OccurrenceReportDescriptorTerm[]>([]);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const _uuidRef = React.useRef(uuid());
    // Mirror of selectedSources for reading inside async callbacks without
    // adding it to effect dependencies (which would refetch on every click)
    const selectedSourcesRef = React.useRef<string[]>([]);
    useEffect(() => {
        selectedSourcesRef.current = selectedSources;
    }, [selectedSources]);
    // We might need workspace context in the future for group selection
    const { workspaceObjects, setCurrentWorkspaceObject, setWorkspaceObjects, getAdditionalResource, publishResourceUpdate } = useWorkspaceContext();

    // Track if initial data has been loaded (prevents double-loading in React 18 Strict Mode)
    const initialDataLoadedRef = React.useRef(false);

    // Load safety models on mount
    useEffect(() => {
        // Prevent double-loading in React 18 Strict Mode (development only)
        if (initialDataLoadedRef.current) {
            return;
        }
        initialDataLoadedRef.current = true; 
        let safetyModelsPromise = SafetyService.getSafetyModels();
        let publishedDescriptorsPromise = OccurrenceReportDescriptorService.fetchPublishedDescriptors({ size: 10000, offset: 0, sort: 'name', direction: 'asc' });

        // Wait for all promises to resolve
        setRunning(true);
        Promise.all([safetyModelsPromise, publishedDescriptorsPromise])
            .then(([allSafetyModels, allPublishedDescriptors]) => {
                // All data has been loaded successfully
                setSafetyModels(allSafetyModels);
                setPublishedDescriptors(allPublishedDescriptors.content || []);
            })
            .catch((err) => {
                // Handle any errors that occurred during loading
                // eslint-disable-next-line no-console
                console.log('Error loading data:', err);
            }).finally(() => {
                setRunning(false);
            });
    }, []);
    
    // Track the current IRP ID to detect when editing a different one
    const currentIRPIdRef = React.useRef<number | undefined>(integratedRiskPicture?.id);
    // Track if we've initialized the basic form fields yet
    const initializedRef = React.useRef(false);
    // Track criteria initialization separately: the tab can open with a summary
    // object that has an id but no reportSearchCriteria, with the full object
    // arriving asynchronously. Criteria must initialize whenever they first
    // appear for the current IRP, not only on the first render with an id.
    const criteriaInitializedRef = React.useRef(false);

    // Update form state when the integratedRiskPicture prop changes
    useEffect(() => {
        if (!integratedRiskPicture || !integratedRiskPicture.id) {
            return;
        }

        if (currentIRPIdRef.current !== integratedRiskPicture.id) {
            initializedRef.current = false;
            criteriaInitializedRef.current = false;
            currentIRPIdRef.current = integratedRiskPicture.id;
        }

        if (!initializedRef.current) {
            initializedRef.current = true;
            // eslint-disable-next-line no-console
            console.log('[IRP Init] Initializing IRP:', integratedRiskPicture.id);

            // Update basic fields
            setName(integratedRiskPicture.name || '');
            setDescription(integratedRiskPicture.description || '');

            // Update event sequence selection
            // Try to get eventSequenceId from either direct field or simpleEventSequences array
            let eventSeqId: number | undefined = integratedRiskPicture.eventSequenceId;

            if (!eventSeqId && (integratedRiskPicture as any).simpleEventSequences?.length > 0) {
                eventSeqId = (integratedRiskPicture as any).simpleEventSequences[0].eventSequenceId;
            }

            if (eventSeqId) {
                setSelectedEventSequenceId(eventSeqId.toString());
                // The matching safety model is resolved in a separate effect
                // once safetyModels are loaded
            } else {
                // eslint-disable-next-line no-console
                console.log('[IRP Init] WARNING: No event sequence ID in IRP data');
            }
        }

        if (integratedRiskPicture.reportSearchCriteria && !criteriaInitializedRef.current) {
            criteriaInitializedRef.current = true;
            const criteria = integratedRiskPicture.reportSearchCriteria;

            if (criteria.includeAllReports) {
                // Legacy drafts can carry filters alongside includeAllReports.
                // The backend ignores them entirely in that mode, so don't
                // restore them into the form either.
                setIncludeAllReports(true);
            } else {
                setIncludeAllReports(false);
                setSelectedSources(criteria.sources || []);

                if (criteria.descriptorTermIds) {
                    // Filter out null/undefined values before converting to strings
                    const validIds = criteria.descriptorTermIds.filter(id => id !== null && id !== undefined);
                    setSelectedDescriptorTermIds(validIds.map(id => id.toString()));
                }

                if (criteria.startDate) {
                    setStartDate(dayjs(criteria.startDate));
                }
                if (criteria.endDate) {
                    setEndDate(dayjs(criteria.endDate));
                }
            }

            if (criteria.limit !== undefined && criteria.limit !== null) {
                setReportLimit(criteria.limit);
            } else {
                setReportLimit(null);
            }
        }
    }, [integratedRiskPicture]);
    
    // When we have an IRP with eventSequenceId but no safety model selected,
    // we need to find which safety model contains that event sequence
    useEffect(() => {
        // Get eventSequenceId from either direct field or simpleEventSequences array
        const eventSeqId = integratedRiskPicture?.eventSequenceId ||
            (integratedRiskPicture as any)?.simpleEventSequences?.[0]?.eventSequenceId;

        // Also try to get safetyModelId directly from simpleEventSequences
        const safetyModelIdFromSequence = (integratedRiskPicture as any)?.simpleEventSequences?.[0]?.safetyModelId;

        // eslint-disable-next-line no-console
        console.log('[Safety Model Search] Effect triggered:', {
            eventSequenceId: eventSeqId,
            safetyModelIdFromSequence: safetyModelIdFromSequence,
            safetyModelsCount: safetyModels.length,
            selectedSafetyModelId: selectedSafetyModelId
        });

        const findSafetyModelForEventSequence = async (): Promise<void> => {
            // If we have the safety model ID directly from the API, use it
            if (safetyModelIdFromSequence && safetyModels.length > 0) {
                const matchingSafetyModel = safetyModels.find(sm => sm.id === safetyModelIdFromSequence);
                if (matchingSafetyModel) {
                    // eslint-disable-next-line no-console
                    console.log('[Safety Model Search] Found safety model directly from API data:', matchingSafetyModel.id);
                    setSelectedSafetyModelId(matchingSafetyModel.id.toString());
                    return;
                }
            }

            if (eventSeqId && safetyModels.length > 0) {
                // eslint-disable-next-line no-console
                console.log('[Safety Model Search] Starting search for event sequence:', eventSeqId);

                // Check if we already have the correct safety model selected
                if (selectedSafetyModelId) {
                    // eslint-disable-next-line no-console
                    console.log('[Safety Model Search] Checking current selection:', selectedSafetyModelId);
                    try {
                        const sequences = await SafetyService.getEventSequences(parseInt(selectedSafetyModelId));
                        const hasEventSequence = sequences.some((es: EventSequenceSummary) => es.id === eventSeqId);
                        if (hasEventSequence) {
                            // eslint-disable-next-line no-console
                            console.log('[Safety Model Search] Current selection is correct, keeping it');
                            return; // Already have the correct safety model
                        }
                        // eslint-disable-next-line no-console
                        console.log('[Safety Model Search] Current selection does NOT contain event sequence, searching...');
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.log('[Safety Model Search] Error checking current selection:', err);
                        // Continue to search if current selection doesn't have the event sequence
                    }
                }

                // Try each safety model to find which one contains our event sequence
                for (const safetyModel of safetyModels) {
                    try {
                        // eslint-disable-next-line no-console
                        console.log('[Safety Model Search] Checking safety model:', safetyModel.id, safetyModel.name);
                        const sequences = await SafetyService.getEventSequences(safetyModel.id);
                        const hasEventSequence = sequences.some((es: EventSequenceSummary) => es.id === eventSeqId);
                        if (hasEventSequence) {
                            // eslint-disable-next-line no-console
                            console.log('[Safety Model Search] FOUND! Setting safety model to:', safetyModel.id);
                            setSelectedSafetyModelId(safetyModel.id.toString());
                            break;
                        }
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.log('[Safety Model Search] Error checking safety model', safetyModel.id, err);
                        // Ignore errors when searching for the safety model
                    }
                }
            } else {
                // eslint-disable-next-line no-console
                console.log('[Safety Model Search] Skipping - missing data:', {
                    hasEventSequenceId: !!integratedRiskPicture?.eventSequenceId,
                    safetyModelsLoaded: safetyModels.length > 0
                });
            }
        };

        findSafetyModelForEventSequence();
    }, [integratedRiskPicture?.eventSequenceId, safetyModels, selectedSafetyModelId]);
    
    // Load event sequences when safety model is selected
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('[Event Sequences Load] Effect triggered:', {
            selectedSafetyModelId: selectedSafetyModelId,
            selectedEventSequenceId: selectedEventSequenceId
        });

        if (selectedSafetyModelId) {
            // eslint-disable-next-line no-console
            console.log('[Event Sequences Load] Loading event sequences for safety model:', selectedSafetyModelId);
            loadEventSequences(parseInt(selectedSafetyModelId));
        } else {
            // eslint-disable-next-line no-console
            console.log('[Event Sequences Load] No safety model selected, clearing sequences');
            setEventSequences([]);
            // Don't clear selectedEventSequenceId here - it may have been set from loaded IRP data
        }
    }, [selectedSafetyModelId]);
    
    // Reload report sources filtered to the selected group whenever the safety model changes.
    // For an existing IRP the group is on the IRP object; for a new IRP use the safety model's group.
    useEffect(() => {
        const sm = safetyModels.find(sm => sm.id.toString() === selectedSafetyModelId);
        const groupId: number | undefined =
            integratedRiskPicture?.group?.id ?? sm?.group?.id;

        if (groupId === undefined) {
            setReportSources([]);
            return;
        }

        setSourcesLoading(true);
        newOccurrenceService.getReportSources(groupId)
            .then(sources => {
                setReportSources(sources);
                // Sync selectedSources with the new group's available sources.
                // An empty prior selection (new form, or include-all mode) gets
                // all sources. Otherwise keep only still-valid selections and
                // warn about any that were dropped instead of silently
                // broadening the filter to every source.
                const validNames = sources.map((s: {sourceName: string}) => s.sourceName);
                const prev = selectedSourcesRef.current;
                if (prev.length === 0) {
                    setSelectedSources(validNames);
                } else {
                    const kept = prev.filter((s: string) => validNames.includes(s));
                    const dropped = prev.filter((s: string) => !validNames.includes(s));
                    if (dropped.length > 0) {
                        toast.warn(`Report source${dropped.length > 1 ? 's' : ''} no longer available for this group and removed from the criteria: ${dropped.join(', ')}`);
                        setSelectedSources(kept);
                    }
                }
            })
            .catch(() => {
                setReportSources([]);
            })
            .finally(() => {
                setSourcesLoading(false);
            });
    }, [selectedSafetyModelId, safetyModels, integratedRiskPicture?.group?.id]);

    // Load event descriptor terms when descriptor is selected
    useEffect(() => {
        if (selectedDescriptorId) {
            loadDescriptorTerms(parseInt(selectedDescriptorId));
        } else {
            setDescriptorTerms([]);
            // Don't clear selectedDescriptorTermIds here - they may have been set from loaded IRP data
        }
    }, [selectedDescriptorId]);

    // Debounced effect to fetch report count preview
    useEffect(() => {
        // Only fetch count for DRAFT IRPs or new IRPs (not read-only)
        const currentIsReadOnly = integratedRiskPicture?.id !== undefined && integratedRiskPicture?.status !== 'DRAFT';
        if (currentIsReadOnly) {
            return;
        }

        // Group comes from the safety model — fetch as soon as one is selected.
        // ESD choice does not affect which reports are counted.
        if (!selectedSafetyModelId) {
            setReportCount(null);
            setEstimatedTime(null);
            return;
        }

        // Wait until source availability for this group has been determined.
        // Fetching while loading would produce a stale count.
        if (sourcesLoading) {
            setReportCount(null);
            setEstimatedTime(null);
            return;
        }

        // If there are no accessible sources the Report Selection Criteria section
        // already shows a dedicated notice — skip the fetch to avoid a second,
        // duplicate "no reports visible" warning in the Analysis Preview.
        if (reportSources.length === 0) {
            setReportCount(null);
            setEstimatedTime(null);
            return;
        }

        // Don't fetch if no sources selected (invalid state)
        if (!includeAllReports && selectedSources.length === 0) {
            setReportCount(null);
            setEstimatedTime(null);
            setCountLoading(false);
            return;
        }

        // Debounce the API call
        const timeoutId = setTimeout(async () => {
            try {
                setCountLoading(true);

                // Resolve the group ID for membership-based report filtering.
                // For an existing draft IRP the group is already on the IRP object.
                // For a new IRP use the selected safety model's group (which will
                // become the IRP's group when it is created).
                const selectedSafetyModel = safetyModels.find(
                    sm => sm.id.toString() === selectedSafetyModelId
                );
                const groupId: number | undefined =
                    integratedRiskPicture?.group?.id ?? selectedSafetyModel?.group?.id;

                // Call API to get count
                const count = await IRPService.previewReportCount(buildReportSearchCriteria(groupId));
                setReportCount(count);

                if (count > 0) {
                    const waves = Math.ceil(count / PARALLEL_REPORT_COUNT);
                    const totalSeconds = waves * AVERAGE_WAVE_SECONDS;
                    const minSeconds = Math.max(totalSeconds * 0.8, MIN_PIPELINE_SECONDS);
                    const maxSeconds = Math.max(totalSeconds * 1.2, MAX_PIPELINE_SECONDS);
                    const minMinutes = Math.floor(minSeconds / 60);
                    const maxMinutes = Math.ceil(maxSeconds / 60);

                    if (maxMinutes <= 1) {
                        setEstimatedTime('About 1 minute');
                    } else if (minMinutes >= maxMinutes) {
                        setEstimatedTime(formatDuration(maxMinutes));
                    } else {
                        setEstimatedTime(`${formatDuration(Math.max(minMinutes, 1))} - ${formatDuration(maxMinutes)}`);
                    }
                } else {
                    setEstimatedTime(null);
                }
            } catch (err: any) {
                // eslint-disable-next-line no-console
                console.warn('Failed to fetch report count preview:', err);
                setReportCount(null);
                setEstimatedTime(null);
            } finally {
                setCountLoading(false);
            }
        }, 500); // 500ms debounce delay

        // Cleanup function to cancel pending API calls
        return (): void => {
            clearTimeout(timeoutId);
        };
    }, [includeAllReports, selectedSources, selectedDescriptorTermIds, startDate, endDate, reportLimit, selectedSafetyModelId, safetyModels, integratedRiskPicture?.id, integratedRiskPicture?.group?.id, integratedRiskPicture?.status, sourcesLoading, reportSources]);

    const loadEventSequences = async (safetyModelId: number): Promise<void> => {
        try {
            setRunning(true);
            // eslint-disable-next-line no-console
            console.log('[Load Event Sequences] Fetching for safety model:', safetyModelId);
            const response = await SafetyService.getEventSequences(safetyModelId);
            // eslint-disable-next-line no-console
            console.log('[Load Event Sequences] Loaded', response?.length || 0, 'sequences');
            setEventSequences(response || []);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to load event sequences:', err);
            setEventSequences([]);
        } finally {
            setRunning(false);
        }
    };

    const loadDescriptorTerms = async (descriptorId: number): Promise<void> => {
        const descriptor = publishedDescriptors.find(desc => desc.id === descriptorId);
        if (descriptor) {
            setDescriptorTerms(descriptor.descriptorTerms || []);
        } else {
            setSelectedDescriptorTermIds([]);
            setDescriptorTerms([]);
        }
    };

    /**
     * Builds a render list with dividers inserted between different groups
     * @param sortedModels - Array of safety models already sorted by group path
     * @returns Array of render items containing both models and dividers
     */
    const buildRenderListWithDividers = (sortedModels: SafetyModel[]): SafetyModelRenderItem[] => {
        const renderList: SafetyModelRenderItem[] = [];
        let previousGroupPath: string | null = null;

        sortedModels.forEach((model) => {
            const groupPath = buildGroupPathString(model.group);

            // Insert divider when group path changes
            if (previousGroupPath !== null && previousGroupPath !== groupPath) {
                renderList.push({
                    isDivider: true,
                    key: `divider-${groupPath}`,
                });
            }

            // Add the model item
            renderList.push({
                isDivider: false,
                key: model.id.toString(),
                model: model,
                groupPath: groupPath,
            });

            previousGroupPath = groupPath;
        });

        return renderList;
    };

    // Compute sorted safety models with dividers for dropdown rendering
    const sortedSafetyModelsWithDividers = useMemo((): SafetyModelRenderItem[] => {
        const sorted = sortSafetyModelsByGroupPath(safetyModels);
        return buildRenderListWithDividers(sorted);
    }, [safetyModels]);

    // Term details keyed by term ID across all published descriptors
    const termDetailsById = useMemo((): Map<string, {name: string; definition: string}> => {
        const map = new Map<string, {name: string; definition: string}>();
        publishedDescriptors.forEach(descriptor => {
            descriptor.descriptorTerms?.forEach(term => {
                if (term && term.id !== null && term.id !== undefined && term.term) {
                    map.set(term.id.toString(), { name: term.term, definition: term.definition || '' });
                }
            });
        });
        return map;
    }, [publishedDescriptors]);

    // Chips are derived from the selected term IDs; there is no separate
    // display state to fall out of sync with the criteria that are saved
    const selectedTermChips = useMemo((): DescriptorTermChip[] =>
        selectedDescriptorTermIds.map(id => ({
            id,
            name: termDetailsById.get(id)?.name ?? `Term ${id}`,
            definition: termDetailsById.get(id)?.definition ?? ''
        })), [selectedDescriptorTermIds, termDetailsById]);

    /**
     * Builds the criteria sent to both the preview count and save endpoints.
     * When includeAllReports is set, all other filters are omitted because the
     * backend ignores them in that mode; persisting them would only create
     * inconsistent drafts. Dates expand to whole days so reports occurring on
     * the end date are included.
     */
    const buildReportSearchCriteria = (groupId?: number): IRPReportSearchCriteria => ({
        includeAllReports: includeAllReports,
        sources: includeAllReports ? undefined : selectedSources,
        descriptorTermIds: includeAllReports || selectedDescriptorTermIds.length === 0
            ? undefined
            : selectedDescriptorTermIds.map(id => parseInt(id)),
        startDate: includeAllReports ? undefined : startDate?.startOf('day').toISOString(),
        endDate: includeAllReports ? undefined : endDate?.endOf('day').toISOString(),
        limit: reportLimit !== null ? reportLimit : undefined,
        groupId: groupId
    });

    const handleClearFilters = (): void => {
        setSelectedSources([]);
        setSelectedDescriptorId('');
        setSelectedDescriptorTermIds([]);
        setDescriptorTerms([]);
        setStartDate(null);
        setEndDate(null);
    };

    const handleSafetyModelChange = (event: any): void => {
        setSelectedSafetyModelId(event.target.value as string);
        setSelectedEventSequenceId('');
        setIncludeAllReports(true);
        handleClearFilters();
    };

    const handleDescriptorChange = (event: any): void => {
        setSelectedDescriptorId(event.target.value as string);
    };

    const handleSourceToggle = (source: string): void => {
        setSelectedSources((prev: string[]): string[] => {
            if (prev.includes(source)) {
                if (prev.length === 1) return prev;
                return prev.filter(s => s !== source);
            }
            return [...prev, source];
        });
    };
    
    const handleRemoveDescriptorTerm = (termId: string): void => {
        setSelectedDescriptorTermIds(prev => prev.filter(id => id !== termId));
    };
    
    const updateIntegratedRiskPicture = (irp: IntegratedRiskPicture): void => {
        let integratedRiskPictureObject = { objectType: ResourceType.INTEGRATED_RISK_PICTURES, object: irp };
        setCurrentWorkspaceObject(integratedRiskPictureObject);
        if (workspaceObjects?.objectType === ResourceType.INTEGRATED_RISK_PICTURES) {
            setWorkspaceObjects({ objectType: ResourceType.INTEGRATED_RISK_PICTURES });
        }

        // Publish update event
        publishResourceUpdate({
            type: ResourceType.INTEGRATED_RISK_PICTURES,
            id: irp.id!,
            updatedData: irp,
            source: 'save'
        });
        updateTab(integratedRiskPictureObject);
    };

    const handleSave = async (): Promise<void> => {
        if (!validateForm()) {
            return;
        }
        
        try {
            setRunning(true);
            setError(null);
            
            const reportSearchCriteria: IRPReportSearchCriteria = buildReportSearchCriteria();

            const dto: CreateIntegratedRiskPictureDTO = {
                name,
                description: description || '',  // Default to empty string since it's required
                eventSequenceIds: [parseInt(selectedEventSequenceId)],  // Changed to array to match backend
                reportSearchCriteria: reportSearchCriteria,
            };

            const sm = safetyModels.find(sm => sm.id.toString() === selectedSafetyModelId);

            if (!sm || !sm.group || !sm.group.id) {
                setError('Selected safety model not found');
                return;
            }

            try {
                setRunning(true);
                const result = integratedRiskPicture?.id ?
                    await IRPService.updateDraftIntegratedRiskPicture(integratedRiskPicture.id, dto)
                    : await IRPService.createIntegratedRiskPicture(sm.group.id, dto);
                updateIntegratedRiskPicture(result);
                toast.success(integratedRiskPicture?.id ? 'Integrated Risk Picture updated successfully' : 'Integrated Risk Picture created successfully');
            } catch (err: any) {
                setError(err.message || integratedRiskPicture?.id ? 'Failed to update Integrated Risk Picture' : 'Failed to create Integrated Risk Picture');
                toast.error(integratedRiskPicture?.id ? 'Failed to update Integrated Risk Picture' : 'Failed to create Integrated Risk Picture');
            } finally {
                setRunning(false);
            }            
        } catch (err: any) {
            setError(err.message || integratedRiskPicture?.id ? 'Failed to update Integrated Risk Picture' : 'Failed to create Integrated Risk Picture');
        } finally {
            setRunning(false);
        }
    };
    
    const handleSaveMetadata = async (): Promise<void> => {
        if (!name.trim()) {
            toast.error('Name is required');
            return;
        }
        try {
            setRunning(true);
            const result = await IRPService.updateIRPMetadata(integratedRiskPicture!.id, name, description);
            updateIntegratedRiskPicture(result);
            toast.success('Integrated Risk Picture updated successfully');
        } catch (err: any) {
            toast.error(err.message || 'Failed to update Integrated Risk Picture');
        } finally {
            setRunning(false);
        }
    };

    const validateForm = (): boolean => {
        // Validation logic - inline errors now displayed via field error/helperText props
        if (!name.trim()) {
            return false;
        }

        if (!selectedEventSequenceId) {
            return false;
        }

        if (!includeAllReports && selectedSources.length === 0) {
            return false;
        }

        if (startDate && endDate && startDate > endDate) {
            return false;
        }

        return true;
    };
    
    // Form is read-only if viewing an existing IRP that is not in DRAFT status
    const isReadOnly = integratedRiskPicture?.id !== undefined && integratedRiskPicture?.status !== 'DRAFT';

    // Name and description can be edited on terminal IRPs (not while queued or running)
    const isMetadataEditable = integratedRiskPicture?.id !== undefined &&
        ['COMPLETED', 'ERROR', 'CANCELLED', 'ARCHIVED'].includes(integratedRiskPicture?.status ?? '');
    
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'auto' }}>
            <Backdrop open={running} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <FormGroup className='formStyle'>
                <Stack className='formControl' spacing={2} sx={{ width: '95%', maxWidth: '1400px' }}>
                    <Typography variant="h6" gutterBottom>
                        {integratedRiskPicture?.id !== undefined 
                            ? (integratedRiskPicture?.status === 'DRAFT' ? 'Edit Draft Integrated Risk Picture' : 'View Integrated Risk Picture')
                            : 'Create Integrated Risk Picture'}
                    </Typography>
                    
                    <Divider />
                    
                    {/* Status display for existing IRPs */}
                    {integratedRiskPicture?.id !== undefined && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Status:
                                </Typography>
                                <Tooltip 
                                    title={
                                        integratedRiskPicture.status === 'DRAFT' ? 'Initial state - IRP can be edited, run, or deleted' :
                                        integratedRiskPicture.status === 'QUEUED' ? 'Ready for processing - waiting to be picked up by the system' :
                                        integratedRiskPicture.status === 'RUNNING' ? 'Currently being processed - analyzing reports and creating mappings' :
                                        integratedRiskPicture.status === 'COMPLETED' ? 'Processing completed successfully - results are available' :
                                        integratedRiskPicture.status === 'ERROR' ? (integratedRiskPicture.errorMessage || 'Processing failed - can be retried or deleted') :
                                        integratedRiskPicture.status === 'CANCELLED' ? 'Processing was cancelled by user' :
                                        integratedRiskPicture.status === 'ARCHIVED' ? 'IRP has been archived and is no longer active' :
                                        'Unknown status'
                                    } 
                                    arrow
                                >
                                    <Chip 
                                        label={integratedRiskPicture.status} 
                                        size="small" 
                                        color={
                                            integratedRiskPicture.status === 'DRAFT' ? 'default' :
                                            integratedRiskPicture.status === 'QUEUED' ? 'warning' :
                                            integratedRiskPicture.status === 'RUNNING' ? 'info' :
                                            integratedRiskPicture.status === 'COMPLETED' ? 'success' :
                                            integratedRiskPicture.status === 'ERROR' ? 'error' :
                                            'secondary'
                                        }
                                    />
                                </Tooltip>
                                {isReadOnly && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        (Read-only: Only DRAFT IRPs can be edited)
                                    </Typography>
                                )}
                            </Box>
                            <Divider />
                        </>
                    )}
                    
                    {/* Basic Information & Analysis Target */}
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2}>
                                <TextField
                                    label="IRP Name"
                                    required
                                    value={name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setName(e.target.value)}
                                    disabled={(isReadOnly && !isMetadataEditable) || running}
                                    size="small"
                                    fullWidth
                                    error={!name.trim() && name !== ''}
                                    helperText={!name.trim() && name !== '' ? 'Name is required' : ''}
                                />

                                <FormControl size="small" fullWidth disabled={isReadOnly || running}>
                                    <InputLabel>Safety Model</InputLabel>
                                    <Select
                                        value={selectedSafetyModelId}
                                        onChange={handleSafetyModelChange}
                                        label="Safety Model"
                                    >
                                        <MenuItem value="">
                                            <em>Select a safety model</em>
                                        </MenuItem>
                                        {sortedSafetyModelsWithDividers.map((item) =>
                                            item.isDivider ? (
                                                <Divider key={item.key} />
                                            ) : (
                                                <MenuItem key={item.model!.id} value={item.model!.id.toString()}>
                                                    <ListItemText
                                                        primary={item.model!.name}
                                                        secondary={item.groupPath}
                                                    />
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth disabled={isReadOnly || running || !selectedSafetyModelId} error={!selectedEventSequenceId}>
                                    <InputLabel>Safety View</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedEventSequenceId ? selectedEventSequenceId.split(',') : []}
                                        onChange={(event: any): void => {
                                            const value = event.target.value as string[];
                                            // LIMITATION: Backend currently supports only single event sequence
                                            // UI displays multi-select for future extensibility
                                            // Enforce single selection by only keeping the most recent selection
                                            setSelectedEventSequenceId(value.length > 0 ? value[value.length - 1] : '');
                                        }}
                                        label="Safety View"
                                        renderValue={(selected: string[]): React.ReactElement => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => (
                                                    <Chip key={value} label={eventSequences.find((es) => es.id.toString() === value)?.name} />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        <MenuItem value="">
                                            <em>Select event sequence</em>
                                        </MenuItem>
                                        {eventSequences.map((es) => (
                                            <MenuItem key={es.id} value={es.id.toString()}>
                                                <Checkbox checked={selectedEventSequenceId.split(',').includes(es.id.toString())} />
                                                <ListItemText primary={es.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {!selectedEventSequenceId && <FormHelperText>Please select an event sequence</FormHelperText>}
                                </FormControl>

                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Description (Optional)"
                                value={description}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setDescription(e.target.value)}
                                disabled={(isReadOnly && !isMetadataEditable) || running}
                                size="small"
                                multiline
                                rows={8}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    
                    <Divider />

                    {/* Report Selection Criteria */}
                    <Typography variant="subtitle1" gutterBottom>
                        Report Selection Criteria
                    </Typography>

                    {isReadOnly ? (
                        // Static summary for non-DRAFT IRPs — plain text, no form controls
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Report scope</Typography>
                                    <Typography variant="body2">
                                        {includeAllReports ? 'All available reports' : 'Filtered selection'}
                                    </Typography>
                                </Box>
                                {reportLimit !== null && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Report limit</Typography>
                                        <Typography variant="body2">{reportLimit.toLocaleString()}</Typography>
                                    </Box>
                                )}
                                {(startDate || endDate) && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Date range</Typography>
                                        <Typography variant="body2">
                                            {startDate ? startDate.format('YYYY-MM-DD') : '—'}{' → '}{endDate ? endDate.format('YYYY-MM-DD') : '—'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {!includeAllReports && selectedSources.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Sources</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                        {selectedSources.map((s) => (
                                            <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {selectedTermChips.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Descriptor filters</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                        {selectedTermChips.map((chip) => (
                                            <Tooltip key={chip.id} title={chip.definition || chip.name} placement="top">
                                                <Chip label={chip.name} size="small" color="primary" variant="outlined" />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    ) : !selectedSafetyModelId && integratedRiskPicture?.group?.id === undefined ? (
                        // No safety model selected and no existing IRP group — prompt user
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            Select a safety model above to configure report selection criteria.
                        </Typography>
                    ) : sourcesLoading ? (
                        // Sources are loading for the selected group
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Loading available report sources...</Typography>
                        </Box>
                    ) : reportSources.length === 0 ? (
                        // No accessible sources for this group
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No report sources are accessible for this group. For reports to appear, the service
                            account for each report source (e.g. NTSB, ASRS) must be a member of the same group
                            as the safety model or one of its parent groups. Contact your system administrator if
                            you believe this is configured incorrectly.
                        </Alert>
                    ) : (
                        // Editable criteria — only group-accessible sources are shown as chips
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeAllReports}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                                setIncludeAllReports(e.target.checked);
                                                if (e.target.checked) {
                                                    setSelectedSources([]);
                                                    setSelectedDescriptorId('');
                                                    setSelectedDescriptorTermIds([]);
                                                    setDescriptorTerms([]);
                                                    setStartDate(null);
                                                    setEndDate(null);
                                                } else {
                                                    setSelectedSources(reportSources.map(s => s.sourceName));
                                                }
                                            }}
                                            disabled={running}
                                        />
                                    }
                                    label="Include all available reports"
                                />
                                <Tooltip title="Limits processing to first N matching reports (useful for testing)" placement="top">
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel id="report-limit-label">Report Limit</InputLabel>
                                        <Select
                                            labelId="report-limit-label"
                                            id="report-limit-select"
                                            value={reportLimit === null ? 'none' : String(reportLimit)}
                                            label="Report Limit"
                                            onChange={(event: SelectChangeEvent<string>): void => {
                                                const value = event.target.value;
                                                setReportLimit(value === 'none' ? null : parseInt(value));
                                            }}
                                            disabled={running}
                                        >
                                            <MenuItem value="none">No limit</MenuItem>
                                            <MenuItem value="1">1</MenuItem>
                                            <MenuItem value="5">5</MenuItem>
                                            <MenuItem value="10">10</MenuItem>
                                            <MenuItem value="25">25</MenuItem>
                                            <MenuItem value="50">50</MenuItem>
                                            <MenuItem value="100">100</MenuItem>
                                            <MenuItem value="250">250</MenuItem>
                                            <MenuItem value="500">500</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Tooltip>
                            </Box>
                            <Collapse in={!includeAllReports}>
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2">
                                            Report Sources:
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={includeAllReports || running}
                                            onClick={handleClearFilters}
                                        >
                                            Clear Filters
                                        </Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {reportSources.map((source) => (
                                            <Chip
                                                key={source.sourceName}
                                                label={source.sourceName}
                                                clickable={!includeAllReports && !running}
                                                color={selectedSources.includes(source.sourceName) ? 'primary' : 'default'}
                                                onClick={(): void => { if (!includeAllReports && !running) handleSourceToggle(source.sourceName); }}
                                                variant={selectedSources.includes(source.sourceName) ? 'filled' : 'outlined'}
                                                sx={{
                                                    opacity: includeAllReports || running ? 0.5 : 1,
                                                    cursor: includeAllReports || running ? 'default' : 'pointer'
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    {!includeAllReports && selectedSources.length === 0 && (
                                        <FormHelperText error>At least one source is required</FormHelperText>
                                    )}
                                    {/* Filter by descriptor terms */}
                                    <Typography variant="body2" gutterBottom>
                                        Filter by Descriptor Terms (Optional):
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {selectedTermChips.map((chip) => (
                                            <Tooltip key={chip.id} title={chip.definition || chip.name} placement="top">
                                                <Chip
                                                    label={chip.name}
                                                    onDelete={!running ? (): void => handleRemoveDescriptorTerm(chip.id) : undefined}
                                                    color="primary"
                                                    variant="filled"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                    <FormControl size="small" fullWidth disabled={includeAllReports || running}>
                                        <InputLabel>Descriptors</InputLabel>
                                        <Select
                                            value={selectedDescriptorId}
                                            onChange={handleDescriptorChange}
                                            label="Descriptors"
                                        >
                                            <MenuItem value="">
                                                <em>Select a descriptor</em>
                                            </MenuItem>
                                            {publishedDescriptors.map((descriptor) => (
                                                <MenuItem key={descriptor.id} value={descriptor.id.toString()}>
                                                    {descriptor.name}{descriptor.version ? ` (v.${descriptor.version})` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth disabled={includeAllReports || running || !selectedDescriptorId}>
                                        <InputLabel>Descriptor Terms</InputLabel>
                                        <Select
                                            multiple
                                            value={selectedDescriptorTermIds}
                                            onChange={(event: SelectChangeEvent<string[]>): void => {
                                                const value = event.target.value;
                                                let newTerms = typeof value === 'string' ? value.split(',') : value;
                                                setSelectedDescriptorTermIds(newTerms);
                                            }}
                                            label="Descriptor Terms"
                                            renderValue={(selected: string[]): React.ReactElement => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((id) => {
                                                        const name = termDetailsById.get(id)?.name;
                                                        return name ? <Chip key={id} label={name} /> : null;
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            <MenuItem value="">
                                                <em>Select descriptor terms</em>
                                            </MenuItem>
                                            {descriptorTerms.map((term) => (
                                                <MenuItem key={term.id} value={term.id!.toString()}>
                                                    <Checkbox checked={selectedDescriptorTermIds.includes(term.id!.toString())} />
                                                    <ListItemText primary={term.term} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                                        Date Range (Optional):
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Stack direction="row" spacing={2}>
                                            <DatePicker
                                                label="Start Date"
                                                value={startDate}
                                                onChange={(newValue: Dayjs | null): void => setStartDate(newValue)}
                                                disabled={includeAllReports || running}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <DatePicker
                                                label="End Date"
                                                value={endDate}
                                                onChange={(newValue: Dayjs | null): void => setEndDate(newValue)}
                                                disabled={includeAllReports || running}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        error: !!(startDate && endDate && startDate.isAfter(endDate)),
                                                        helperText: (startDate && endDate && startDate.isAfter(endDate)) ? 'End date must be after start date' : ''
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </LocalizationProvider>
                                </Stack>
                            </Collapse>
                        </>
                    )}

                    <Divider />
                    
                    {/* Analysis Preview — shown as soon as a safety model (and thus group) is known,
                        but suppressed when sources are empty since the criteria section already
                        explains the situation. ESD selection does not affect the count. */}
                    {!isReadOnly && selectedSafetyModelId && reportSources.length > 0 && (
                        <Alert severity={
                            !includeAllReports && selectedSources.length === 0 ? 'error' :
                            'info'
                        }>
                            <Typography variant="body2">
                                <strong>Analysis Preview:</strong>{' '}
                                {!includeAllReports && selectedSources.length === 0 ? (
                                    'Please select at least one report source'
                                ) : countLoading ? (
                                    <>
                                        <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                                        {' '}Loading report count...
                                    </>
                                ) : reportCount === 0 ? (
                                    'No reports match the current filter criteria. Try broadening your report sources, date range, or descriptor filters.'
                                ) : reportCount !== null ? (
                                    <>
                                        This IRP will analyze <strong>{reportCount.toLocaleString()}</strong> report{reportCount !== 1 ? 's' : ''}
                                        {estimatedTime && (
                                            <>
                                                <br />
                                                Estimated processing time: <strong>{estimatedTime}</strong> (approximate)
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                                        {' '}Calculating report count...
                                    </>
                                )}
                            </Typography>
                        </Alert>
                    )}
                    
                    {/* Action Buttons */}
                    {!isReadOnly && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<SaveAltIcon />}
                                onClick={handleSave}
                                disabled={running || !selectedEventSequenceId || (!includeAllReports && selectedSources.length === 0)}
                            >
                                {integratedRiskPicture?.id !== undefined ? 'Update IRP' : 'Create IRP Analysis'}
                            </Button>
                            {!selectedEventSequenceId && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Please select an event sequence to create an IRP analysis
                                </Typography>
                            )}
                            {selectedEventSequenceId && !includeAllReports && selectedSources.length === 0 && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    Please select at least one report source
                                </Typography>
                            )}
                        </Box>
                    )}
                    {isMetadataEditable && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<SaveAltIcon />}
                                onClick={handleSaveMetadata}
                                disabled={running || !name.trim()}
                            >
                                Save
                            </Button>
                        </Box>
                    )}
                </Stack>
            </FormGroup>
        </Box>
    );
};

export default IntegratedRiskPictureEditor;